import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, ThemedSelect } from '@bizfirst/common-react';
import type { AgentHookDTO, AgentHookFormValues, HookDTO } from '../../types';
import { useAgentHookAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

const EMPTY_FORM: AgentHookFormValues = { HookID: 0, IsActive: true, ScopeMode: '', OwnerScope: '' };

/**
 * Restored Hooks tab (Phase 2 of the Agent-editor sub-entity restoration — see
 * InstructionsPanel.tsx, this panel's direct structural template). Unlike the other restored
 * tabs, there was no standalone old frontend file to adapt for this one — the source AgentEditor
 * had it as an inline section driven by a generic `AssignmentModal` with `entityType="hook"`.
 * This is a fresh, self-contained link/unlink UI over AIAgent_AgentHooks, a plain junction row
 * (AgentID + HookID + IsActive) — confirmed there is no dedicated link/unlink API anywhere on
 * BaseAgentHookController.cs, so "link" is Create and "unlink" is Delete on that row (see
 * agentHookAdminApiClient.ts and types.ts's AgentHookDTO comment). ScopeMode/OwnerScope are
 * real, optional per-agent memory-hook override columns exposed as plain text inputs;
 * ConfigJson (a raw JSON override blob) is deliberately not given a control here, matching
 * InstructionsPanel's own precedent of not building UI for every advanced column.
 */
export function HooksPanel({ agentId }: { agentId: number }) {
  const api = useAgentHookAdminApiClient();
  const [links, setLinks] = useState<AgentHookDTO[]>([]);
  const [availableHooks, setAvailableHooks] = useState<HookDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentHookDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentHookFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentHookDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [linksResult, hooksResult] = await Promise.all([api.getHooksByAgent(agentId), api.getAvailableHooks()]);
    setLinks(linksResult.data ?? []);
    setAvailableHooks(hooksResult.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const hookName = (hookId: number) => availableHooks.find((h) => h.HookID === hookId)?.Name ?? `Hook #${hookId}`;
  const linkedHookIds = new Set(links.map((l) => l.HookID));
  const pickableHooks = availableHooks.filter((h) => !linkedHookIds.has(h.HookID));

  const startEdit = (link: AgentHookDTO | 'new') => {
    setEditing(link);
    setError(null);
    setForm(
      link === 'new'
        ? { ...EMPTY_FORM, HookID: pickableHooks[0]?.HookID ?? 0 }
        : { HookID: link.HookID, IsActive: link.IsActive, ScopeMode: link.ScopeMode ?? '', OwnerScope: link.OwnerScope ?? '' }
    );
  };

  const handleSave = async () => {
    if (!form.HookID) {
      setError('A hook must be selected.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = editing === 'new' ? await api.linkHook(agentId, form) : await api.updateHookLink((editing as AgentHookDTO).AgentHookID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to save.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.unlinkHook(pendingDelete.AgentHookID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setLinks((prev) => prev.filter((l) => l.AgentHookID !== pendingDelete.AgentHookID));
    }
  };

  if (editing) {
    const isNew = editing === 'new';
    return (
      <div className="aaa-form-grid">
        <div className="aaa-form-group aaa-form-group-full">
          <label>Hook *</label>
          {isNew ? (
            <ThemedSelect
              value={form.HookID || ''}
              onChange={(v) => setForm((f) => ({ ...f, HookID: Number(v) }))}
              options={pickableHooks.map((h) => ({ value: h.HookID, label: h.Name }))}
              placeholder="— Select a hook —"
            />
          ) : (
            <input type="text" value={hookName(form.HookID)} disabled />
          )}
        </div>
        <div className="aaa-form-group">
          <label>Scope Mode</label>
          <input
            type="text"
            placeholder="agent / origin"
            value={form.ScopeMode ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, ScopeMode: e.target.value }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Owner Scope</label>
          <input
            type="text"
            placeholder="user / tenant / role / app"
            value={form.OwnerScope ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, OwnerScope: e.target.value }))}
          />
        </div>
        <div className="aaa-form-group">
          <label className="aaa-checkbox">
            <input type="checkbox" checked={form.IsActive} onChange={(e) => setForm((f) => ({ ...f, IsActive: e.target.checked }))} />
            Active
          </label>
        </div>
        {error && <div className="aaa-editor-error aaa-form-group-full">{error}</div>}
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving || !form.HookID}>
            {saving ? 'Saving…' : isNew ? 'Link Hook' : 'Save Hook Link'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')} disabled={loading || pickableHooks.length === 0}>
          <PlusIcon size={13} /> Add Hook
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && links.length === 0 && <div className="aaa-entity-dropdown-empty">No hooks linked yet.</div>}
      <div className="aaa-entity-grid">
        {links.map((link) => (
          <div key={link.AgentHookID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{hookName(link.HookID)}</strong>
              <span className={`aaa-badge ${link.IsActive ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                {link.IsActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {(link.ScopeMode || link.OwnerScope) && (
              <p>
                {link.ScopeMode && `Scope: ${link.ScopeMode}`}
                {link.ScopeMode && link.OwnerScope && ' · '}
                {link.OwnerScope && `Owner: ${link.OwnerScope}`}
              </p>
            )}
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(link)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(link)} title="Unlink">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Unlink Hook"
        message={`Unlink "${pendingDelete ? hookName(pendingDelete.HookID) : ''}" from this agent?`}
        confirmLabel="Unlink"
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
